import classnames from "classnames";
import { TextLink } from "modules/common/components/routing/TextLink";
import { hex2rgba, shadeColor } from "modules/common/services/colors";
import React, { useCallback, useMemo } from "react";
import { blocksOf } from "../models/plan-queue";
import { columnLeft, columnsWidth, PlanGrid } from "../models/plan-scale";
import { CellSelection, isWork, PlanBlock, PlanColumn, PlanProject, PlanRow, PlanSection } from "../models/plan-types";
import { absenceClassName, absenceLabel } from "./plan-absences";
import {
    BLOCK_CLASS,
    columnAt,
    HANDLE_LEFT_CLASS,
    HANDLE_RIGHT_CLASS,
    PAST_BLOCK_CLASS,
    SELECTION_CLASS,
    TRACK_CLASS,
} from "./plan-geometry";
import styles from "./PlanRowView.module.scss";

/** На сколько затемнить цвет проекта для рамки блока */
const BORDER_SHADE = -30;

/**
 * Уже этой ширины подпись в блок не влезает — в недельном масштабе таких много.
 * Порог держится ниже ширины дня (30px): однодневный блок обязан показывать номер,
 * иначе самый частый случай — «залил один день» — остаётся без подписи.
 */
const LABEL_MIN_WIDTH = 26;

/** Ниже этой ширины подпись рисуется компактно: мельче, без отступов, по центру */
const LABEL_TIGHT_WIDTH = 44;

/**
 * Ступени подписи с шифром раздела: обычный шрифт, мельче, ещё мельче без отступов —
 * так же, как номер проекта ужимается в однодневный блок. Ширина символа примерная
 * (цифры моноширинные), отступы — боковые отступы блока. Если подпись не влезает даже
 * на последней ступени, остаётся один номер: обрезанное «0500·К» хуже, чем «0500»
 * с полным шифром в подсказке.
 */
const LABEL_STEPS = [
    { charWidth: 7.5, padding: 8, className: "" },
    { charWidth: 6.2, padding: 2, className: styles.blockLabelSmall },
    { charWidth: 5.5, padding: 1, className: styles.blockLabelTiny },
];

/** Номер проекта и шифр раздела в ячейке разделяются точкой по центру */
const SECTION_SEPARATOR = "·";

const FALLBACK_COLOR = "#9b9b9b";

/** Прозрачность заливки блока — та же, что уходит в hex2rgba */
const BLOCK_ALPHA = 0.75;

/** Ниже этой яркости подложки тёмная подпись сливается с цветом проекта */
const LIGHT_TEXT_THRESHOLD = 150;

/** shadeColor работает только с полным hex — подстраховываемся от пустых значений */
function normalizeColor(color: string): string {
    return /^#[0-9a-f]{6}$/i.test(color) ? color : FALLBACK_COLOR;
}

/**
 * Тёмная ли заливка блока. Считается по цвету, УЖЕ смешанному с белым фоном сетки:
 * блок полупрозрачный, и сам по себе тон проекта здесь ни о чём не говорит.
 */
function isDarkBlock(color: string): boolean {
    const value = parseInt(color.slice(1), 16);
    const mix = (channel: number) => channel * BLOCK_ALPHA + 255 * (1 - BLOCK_ALPHA);

    const r = mix((value >> 16) & 255);
    const g = mix((value >> 8) & 255);
    const b = mix(value & 255);

    return 0.299 * r + 0.587 * g + 0.114 * b < LIGHT_TEXT_THRESHOLD;
}

/** Уже этой ширины номер проекта в полоску перегрузки не влезает */
const OVERLAP_LABEL_MIN_WIDTH = 22;

/** Полоска перегрузки залита чистым цветом проекта — яркость считаем без смешивания с белым */
function isDarkColor(color: string): boolean {
    if (!/^#[0-9a-f]{6}$/i.test(color)) {
        return false;
    }

    const value = parseInt(color.slice(1), 16);

    return 0.299 * ((value >> 16) & 255) + 0.587 * ((value >> 8) & 255) + 0.114 * (value & 255) < LIGHT_TEXT_THRESHOLD;
}

interface PlanRowViewProps {
    row: PlanRow;
    rowIndex: number;
    columns: PlanColumn[];
    projects: Map<string, PlanProject>;
    /** Разделы состава проектов по guid — для подписи и подсказки блока */
    sections: Map<string, PlanSection>;
    /**
     * Строку можно только смотреть: чужой отдел или нет права на правку. У трека и блоков
     * тогда нет классов-крючков interact.js, поэтому ни перенос, ни растягивание, ни рамка
     * выделения, ни бросок чужого блока на эту строку не запускаются.
     */
    readOnly?: boolean;
    /** Строка только для чтения рядом с редактируемыми — целиком в стиле прошлых дней, с ФИО */
    dimmed?: boolean;
    /** Карточка сотрудника; null — нет прав, ФИО остаётся текстом */
    employeeUrl?: string | null;
    selection: CellSelection | null;
    /** Куда встанет перетаскиваемый отрезок; null, если он не над этой строкой */
    dropPreview: CellSelection | null;
    /** Раскладка сетки: офсеты колонок и слоты */
    grid: PlanGrid;
    /** Слот (день или неделя), выделенный кликом по шапке */
    selectedSlot: { start: number; length: number } | null;
    /** Левые края разделителей месяцев, px */
    monthBreaks: number[];
    onBlockClick: (rowIndex: number, start: number, length: number) => void;
    /** Двойной клик по любому дню строки — открыть выбор проекта */
    onDayActivate: (rowIndex: number, column: number, x: number, y: number) => void;
    /** Правый клик по дню — контекстное меню */
    onDayContext: (rowIndex: number, column: number, x: number, y: number) => void;
}

export const PlanRowView: React.FC<PlanRowViewProps> = React.memo((props) => {
    const {
        row,
        rowIndex,
        columns,
        projects,
        sections,
        readOnly,
        dimmed,
        employeeUrl,
        selection,
        dropPreview,
        grid,
        selectedSlot,
        monthBreaks,
        onBlockClick,
        onDayActivate,
        onDayContext,
    } = props;

    const onDoubleClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const track = event.currentTarget;
            const column = columnAt(track, event.clientX, grid);
            const rect = track.getBoundingClientRect();

            onDayActivate(rowIndex, column, event.clientX, rect.bottom + 4);
        },
        [grid, rowIndex, onDayActivate]
    );

    const onContextMenu = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();

            const column = columnAt(event.currentTarget, event.clientX, grid);
            onDayContext(rowIndex, column, event.clientX, event.clientY);
        },
        [grid, rowIndex, onDayContext]
    );

    const blocks = useMemo(() => blocksOf(row, columns), [row, columns]);

    /** Прошлое приглушается до начала текущего слота — неделя целиком не «прошлая» */
    const pastWidth = useMemo(() => {
        const first = grid.slots.find((slot) => !slot.isPast);
        return first ? first.left : grid.totalWidth;
    }, [grid]);

    /** Сегодняшний день, а в недельном масштабе — вся текущая неделя */
    const today = useMemo(() => grid.slots.find((slot) => slot.isToday) || null, [grid]);

    /**
     * Выделение, совпадающее с блоком целиком, не должно перехватывать мышь:
     * иначе под ним недоступны ни тело блока, ни ручки растягивания. Оно тогда
     * рисуется просто рамкой, а жесты достаются самому блоку.
     */
    const selectionIsBlock = useMemo(() => {
        if (!selection || selection.rowIndex !== rowIndex) {
            return false;
        }

        return blocks.some(
            (block) => block.start === selection.start && block.length === selection.length
        );
    }, [selection, rowIndex, blocks]);

    // под местом приземления уже есть работы — предупреждаем, что они заменятся
    const overwrites = useMemo(() => {
        if (!dropPreview) {
            return false;
        }

        for (let i = dropPreview.start; i < dropPreview.start + dropPreview.length; i++) {
            if (isWork(row.cells[i])) {
                return true;
            }
        }

        return false;
    }, [dropPreview, row.cells]);

    return (
        <div className={classnames(styles.row, { [styles.rowDimmed]: dimmed })}>
            <div className={styles.name} title={`${row.label} — ${row.position}`}>
                <div className={styles.label}>
                    <TextLink to={employeeUrl || null}>{row.label}</TextLink>
                </div>
                <div className={styles.position}>{row.position}</div>
            </div>

            <div
                className={classnames(styles.track, {
                    [TRACK_CLASS]: !readOnly,
                    [styles.trackReadOnly]: readOnly,
                })}
                data-row={rowIndex}
                style={
                    {
                        width: grid.totalWidth,
                        // полоски фона идут по границам слотов — как колонки в шапке
                        "--wp-grid-step": `${grid.slotWidth}px`,
                    } as React.CSSProperties
                }
                onDoubleClick={onDoubleClick}
                onContextMenu={onContextMenu}
            >
                {(dimmed || pastWidth > 0) && (
                    <div className={styles.past} style={{ width: dimmed ? grid.totalWidth : pastWidth }} />
                )}

                {today && <div className={styles.today} style={{ left: today.left, width: today.width }} />}

                {selectedSlot && (
                    <div
                        className={styles.columnMark}
                        style={{
                            left: columnLeft(grid, selectedSlot.start),
                            width: columnsWidth(grid, selectedSlot.start, selectedSlot.length),
                        }}
                    />
                )}

                {/* неделя без рабочих дней: ставить в неё нечего */}
                {grid.slots.map((slot) =>
                    slot.isEmpty ? (
                        <div
                            key={`empty_${slot.left}`}
                            className={styles.emptyWeek}
                            style={{ left: slot.left, width: slot.width }}
                            title="Рабочих дней на этой неделе нет"
                        />
                    ) : null
                )}

                {monthBreaks.map((left) => (
                    <div key={left} className={styles.monthBreak} style={{ left }} />
                ))}

                {blocks.map((block) => {
                    const absence = block.absence;
                    const project = absence ? undefined : projects.get(block.orderId);
                    const section = absence ? undefined : sectionOf(block, sections);
                    const color = normalizeColor(project ? project.color : "");
                    const left = columnLeft(grid, block.start);
                    const width = columnsWidth(grid, block.start, block.length);
                    const tip = absence ? null : blockTip(project, section);
                    const label = blockLabel(project, section, width);

                    return (
                        <div
                            key={`${absence ? absence.type : `${block.orderId}_${block.sectionId || ""}`}_${block.start}`}
                            className={classnames(styles.block, {
                                // у прошлых блоков свой класс: перетаскивание для них
                                // не регистрируется, а растягивание вправо — да
                                [BLOCK_CLASS]: !readOnly && !block.isPast,
                                [PAST_BLOCK_CLASS]: !readOnly && block.isPast,
                                [styles.blockReadOnly]: readOnly,
                                [styles.blockPast]: block.isPast || dimmed,
                                [styles.blockTight]: !absence && (width < LABEL_TIGHT_WIDTH || !!label.step),
                                [styles.blockDark]: !absence && isDarkBlock(color),
                                // вид отсутствия — общий с «Плановым графиком работ»
                                [absence ? absenceClassName(absence.type) : ""]: !!absence,
                            })}
                            data-row={rowIndex}
                            data-start={block.start}
                            data-length={block.length}
                            style={
                                absence
                                    ? { left, width }
                                    : {
                                          left,
                                          width,
                                          backgroundColor: hex2rgba(color, BLOCK_ALPHA),
                                          borderColor: shadeColor(color, BORDER_SHADE),
                                      }
                            }
                            data-tip={absence ? absence.type : tip ? tip.title : ""}
                            data-tip-note={tip ? tip.note : ""}
                            onClick={() => onBlockClick(rowIndex, block.start, block.length)}
                        >
                            {!readOnly && !block.isPast && (
                                <span className={`${HANDLE_LEFT_CLASS} ${styles.handleLeft}`} />
                            )}

                            {/* у отсутствия одна подпись на блок, как на «Плановом графике работ»:
                                «Отпуск», если влезает, иначе «О» */}
                            {absence
                                ? absenceLabel(absence.type, absence.shortName, width)
                                : width >= LABEL_MIN_WIDTH && (
                                      <span className={classnames(styles.blockLabel, label.className)}>{label.text}</span>
                                  )}

                            {!readOnly && <span className={`${HANDLE_RIGHT_CLASS} ${styles.handleRight}`} />}
                        </div>
                    );
                })}

                {/*
                    Наложение: в дне стоит ещё один проект (перегрузка). Он закреплён за датой —
                    полоска его цвета внизу дня; сохранение дня его не теряет. Сам день — под
                    красной штриховкой, как любой конфликт на «Плановом графике работ».
                */}
                {row.overlaps &&
                    Object.keys(row.overlaps).map((key) => {
                        const column = +key;
                        const extra = row.overlaps![column];

                        if (!extra.length || column >= columns.length || columns[column].hidden) {
                            return null;
                        }

                        const first = projects.get(extra[0].orderId);
                        const width = Math.max(2, columnsWidth(grid, column, 1) - 4);
                        const caption = first
                            ? extra.length > 1
                                ? `${first.fullInventoryNumber}+${extra.length - 1}`
                                : first.fullInventoryNumber
                            : "";
                        const names = extra
                            .map((cell) => {
                                const project = projects.get(cell.orderId);
                                const section = cell.sectionId ? sections.get(cell.sectionId) : undefined;
                                const number = project ? project.fullInventoryNumber : "";

                                return section && section.code ? `${number}·${section.code}` : number;
                            })
                            .join(", ");

                        return (
                            <React.Fragment key={`overlap_${column}`}>
                                <div
                                    className={styles.conflictDay}
                                    style={{ left: columnLeft(grid, column), width: columnsWidth(grid, column, 1) }}
                                />
                                <div
                                    className={classnames(styles.overlap, {
                                        [styles.overlapDark]: !!first && isDarkColor(first.color),
                                    })}
                                    style={{
                                        left: columnLeft(grid, column) + 2,
                                        width,
                                        backgroundColor: first ? first.color : undefined,
                                    }}
                                    data-tip="Перегрузка: в этот день ещё проект"
                                    data-tip-note={names}
                                >
                                    {width >= OVERLAP_LABEL_MIN_WIDTH && caption}
                                </div>
                            </React.Fragment>
                        );
                    })}

                {dropPreview && (
                    <div
                        className={classnames(styles.dropGhost, {
                            [styles.overwrites]: overwrites,
                        })}
                        style={{
                            left: columnLeft(grid, dropPreview.start),
                            width: columnsWidth(grid, dropPreview.start, dropPreview.length),
                        }}
                    />
                )}

                {selection && selection.rowIndex === rowIndex && (
                    <div
                        className={classnames(styles.selection, {
                            [SELECTION_CLASS]: !selectionIsBlock,
                            [styles.selectionPassive]: selectionIsBlock,
                        })}
                        data-row={rowIndex}
                        data-start={selection.start}
                        data-length={selection.length}
                        style={{
                            left: columnLeft(grid, selection.start),
                            width: columnsWidth(grid, selection.start, selection.length),
                        }}
                    />
                )}
            </div>
        </div>
    );
});

PlanRowView.displayName = "PlanRowView";

function sectionOf(block: PlanBlock, sections: Map<string, PlanSection>): PlanSection | undefined {
    return block.sectionId ? sections.get(block.sectionId) : undefined;
}

/** Номер проекта, а если хватает ширины — ещё и шифр раздела: «0500·КР3» */
/**
 * Подпись блока: номер проекта, а с разделом — «0500·КР3», ужатый до той ступени шрифта,
 * на которой он влезает целиком. `step` > 0 — подпись мельче обычной, блок тогда без отступов.
 */
function blockLabel(
    project: PlanProject | undefined,
    section: PlanSection | undefined,
    width: number
): { text: string; className: string; step: number } {
    const plain = { text: project ? project.fullInventoryNumber : "—", className: "", step: 0 };

    if (!project || !section || !section.code) {
        return plain;
    }

    const text = `${project.fullInventoryNumber}${SECTION_SEPARATOR}${section.code}`;
    const step = LABEL_STEPS.findIndex((item) => width >= item.padding + text.length * item.charWidth);

    return step < 0 ? plain : { text, className: LABEL_STEPS[step].className, step };
}

/** Подсказка блока: номер проекта с полным шифром раздела, ниже — название и описание раздела */
function blockTip(
    project: PlanProject | undefined,
    section: PlanSection | undefined
): { title: string; note: string } | null {
    if (!project) {
        return null;
    }

    if (!section) {
        return { title: project.fullInventoryNumber, note: project.name };
    }

    const description = section.description.trim();
    const parts = [project.name, description, section.inactive ? "Раздел удалён из состава проекта" : ""];

    return {
        title: `${project.fullInventoryNumber} · ${section.name}`,
        note: parts.filter((part) => !!part).join(" — "),
    };
}
